import { useMemo } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { formatPercentValue } from "~/utils/functions/formatPercentValue";
import {
    ReturnVolatilityChartStats,
    computeReturnVolatilityDensityChart,
    formatBasisPoints,
    formatSignedBasisPoints,
    formatXAxisBpLabel,
} from "./util";
import { TimeSeriesPoint } from "~/modules/asset_timeseries_v4/types";

// // // //

function formatSinceInceptionPeriod(
    periodStart: string | null,
    periodEnd: string | null,
): string | null {
    if (!periodStart || !periodEnd) {
        return null;
    }

    const formatDate = (value: string) =>
        new Date(value).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
        });

    return `Since inception (${formatDate(periodStart)} – ${formatDate(periodEnd)})`;
}

const CHART_FONT_FAMILY = '"Lausanne", ui-sans-serif, system-ui, sans-serif';

/** Shorter than the other performance tabs' 320px charts — the stats row above eats into the shared 400px panel. */
const VOLATILITY_CHART_HEIGHT = 300;

const VOLATILITY_CHART_COLORS = {
    bar: "#94a3b8",
    barBorder: "#ffffff",
    kde: "#334155",
    shade: "#f1f5f9",
    zeroLine: "#64748b",
    axis: "#64748b",
    grid: "#e2e8f0",
    statsBorder: "#e2e8f0",
    statsText: "#334155",
};

const SQUARE_COLUMN_BORDER_RADIUS = {
    radius: 0,
    scope: "point" as const,
    where: "end" as const,
};

function VolatilityStat(props: {
    label: string;
    value: string;
    detail?: string;
}) {
    const { label, value, detail } = props;

    return (
        <div className="min-w-0">
            <p className="text-xs font-normal text-tertiary-foreground">
                {label}
            </p>
            <p className="mt-0.5 text-sm font-medium tabular-nums text-foreground">
                {value}
            </p>
            {detail && (
                <p className="mt-0.5 text-xs tabular-nums text-tertiary-foreground">
                    {detail}
                </p>
            )}
        </div>
    );
}

function VolatilitySummary(props: { stats: ReturnVolatilityChartStats }) {
    const { stats } = props;

    const rangeLabel = `${formatSignedBasisPoints(stats.percentile99LowBp)} to ${formatSignedBasisPoints(stats.percentile99HighBp)} bp`;

    return (
        <div
            className="border-b border-muted pb-3"
            aria-label="Return volatility summary statistics"
        >
            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                <VolatilityStat
                    label="Daily σ"
                    value={`${formatBasisPoints(stats.sigmaBpPerDay)} bp`}
                    detail={`${formatPercentValue(stats.sigmaAnnualizedPercent)}% ann.`}
                />
                <VolatilityStat
                    label="Largest day"
                    value={`${formatBasisPoints(stats.maxAbsMoveBp)} bp`}
                />
                <VolatilityStat label="99% of days" value={rangeLabel} />
            </div>
        </div>
    );
}

/**
 * Return volatility distribution for the asset screener — helps investors compare
 * how stable day-to-day NAV moves are across products.
 */
export function ReturnVolatilityHistogramChart(props: {
    points: TimeSeriesPoint[];
    isLoading: boolean;
    assetName: string;
    showHeader?: boolean;
}) {
    const { points, isLoading, assetName, showHeader = true } = props;

    const chartData = useMemo(
        () => computeReturnVolatilityDensityChart(points),
        [points],
    );

    const chartOptions = useMemo((): Highcharts.Options => {
        const {
            bins,
            kdePoints,
            stats,
            xMinBp,
            xMaxBp,
            binWidthBp,
            xAxisTickPositionsBp,
        } = chartData;

        return {
            chart: {
                height: VOLATILITY_CHART_HEIGHT,
                // Zero side spacing to stay flush with the asset card's section grid
                spacing: [8, 0, 20, 0],
                style: {
                    fontFamily: CHART_FONT_FAMILY,
                },
                backgroundColor: "transparent",
            },
            title: {
                text: undefined,
            },
            credits: {
                enabled: false,
            },
            legend: {
                enabled: false,
            },
            tooltip: {
                shared: false,
                useHTML: true,
                borderWidth: 1,
                borderColor: VOLATILITY_CHART_COLORS.statsBorder,
                backgroundColor: "#ffffff",
                style: {
                    fontFamily: CHART_FONT_FAMILY,
                    fontSize: "0.75rem",
                    color: VOLATILITY_CHART_COLORS.statsText,
                },
                formatter: function formatter() {
                    const pointIndex =
                        typeof this.x === "number" ? this.x : Number(this.x);
                    const binIndex = Math.min(
                        Math.max(
                            0,
                            Math.floor((pointIndex - xMinBp) / binWidthBp),
                        ),
                        bins.length - 1,
                    );
                    const bin = bins[binIndex];

                    if (!bin) {
                        return false;
                    }

                    return `<span style="font-weight:500">${formatSignedBasisPoints(bin.minBp)} to ${formatSignedBasisPoints(bin.maxBp)} bp</span><br/>${bin.count} day${bin.count === 1 ? "" : "s"}`;
                },
            },
            xAxis: {
                type: "linear",
                min: xMinBp,
                max: xMaxBp,
                tickPositions: xAxisTickPositionsBp,
                lineColor: VOLATILITY_CHART_COLORS.grid,
                tickColor: VOLATILITY_CHART_COLORS.grid,
                gridLineColor: VOLATILITY_CHART_COLORS.grid,
                gridLineWidth: 1,
                title: {
                    text: "Daily return (bp)",
                    margin: 10,
                    style: {
                        color: VOLATILITY_CHART_COLORS.axis,
                        fontSize: "0.75rem",
                        fontFamily: CHART_FONT_FAMILY,
                    },
                },
                labels: {
                    formatter: function formatter() {
                        return formatXAxisBpLabel(this.value);
                    },
                    style: {
                        color: VOLATILITY_CHART_COLORS.axis,
                        fontSize: "0.75rem",
                        fontFamily: CHART_FONT_FAMILY,
                    },
                },
                plotBands: stats
                    ? [
                          {
                              from: stats.percentile99LowBp,
                              to: stats.percentile99HighBp,
                              color: VOLATILITY_CHART_COLORS.shade,
                              zIndex: 0,
                          },
                      ]
                    : [],
                plotLines: [
                    {
                        value: 0,
                        color: VOLATILITY_CHART_COLORS.zeroLine,
                        width: 1,
                        zIndex: 4,
                    },
                ],
            },
            yAxis: {
                min: 0,
                title: {
                    text: undefined,
                },
                gridLineColor: VOLATILITY_CHART_COLORS.grid,
                labels: {
                    enabled: false,
                },
            },
            plotOptions: {
                column: {
                    borderRadius: SQUARE_COLUMN_BORDER_RADIUS,
                    borderWidth: 1,
                    borderColor: VOLATILITY_CHART_COLORS.barBorder,
                    minPointLength: 0,
                    pointPadding: 0.04,
                    groupPadding: 0,
                    crisp: true,
                    states: {
                        hover: {
                            color: "#64748b",
                        },
                    },
                },
                spline: {
                    marker: {
                        enabled: false,
                    },
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
            },
            series: [
                {
                    type: "column",
                    name: "Daily return density",
                    color: VOLATILITY_CHART_COLORS.bar,
                    borderRadius: SQUARE_COLUMN_BORDER_RADIUS,
                    borderWidth: 1,
                    borderColor: VOLATILITY_CHART_COLORS.barBorder,
                    minPointLength: 0,
                    zIndex: 1,
                    data: bins.map((bin) => ({
                        x: bin.midpointBp,
                        y: bin.density,
                    })),
                    pointRange: binWidthBp,
                },
                {
                    type: "spline",
                    name: "Density estimate",
                    color: VOLATILITY_CHART_COLORS.kde,
                    lineWidth: 2,
                    zIndex: 2,
                    enableMouseTracking: false,
                    data: kdePoints.map((point) => [point.xBp, point.density]),
                },
            ],
        };
    }, [chartData]);

    const sinceInceptionPeriod =
        chartData.stats === null
            ? null
            : formatSinceInceptionPeriod(
                  chartData.stats.periodStart,
                  chartData.stats.periodEnd,
              );

    return (
        <div>
            {showHeader && (
                <p className="text-sm font-medium text-foreground">
                    Return Volatility
                </p>
            )}

            {isLoading && (
                <p className="mt-3 text-sm text-tertiary-foreground">
                    Loading chart...
                </p>
            )}

            {!isLoading &&
                !chartData.hasSufficientData &&
                chartData.insufficientDataMessage && (
                    <p className="mt-3 text-sm text-tertiary-foreground">
                        {chartData.insufficientDataMessage} ({assetName})
                    </p>
                )}

            {!isLoading && chartData.hasSufficientData && chartData.stats && (
                <div className="space-y-3">
                    <VolatilitySummary stats={chartData.stats} />

                    <HighchartsReact
                        highcharts={Highcharts}
                        options={chartOptions}
                    />

                    {sinceInceptionPeriod && (
                        <p className="text-xs text-tertiary-foreground">
                            {sinceInceptionPeriod}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
